import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { useMutation } from "@tanstack/react-query";
import {
  Building2,
  Calendar,
  Clock,
  CreditCard,
  Eye,
  EyeOff,
  FileText,
  Globe,
  LayoutList,
  Mail,
  MessageSquare,
  NotebookPen,
  Phone,
  ShoppingBag,
  User,
  Users,
} from "lucide-react";
import { toastApiError } from "@/lib/errorMessages";
import { resolveLocale, supportedLanguageLabel } from "@/lib/locales";
import { Button, ConfirmModal, DetailRow, EmptyState, Tabs } from "@/components/ui";
import { DetailTabPanel, SectionCard } from "@/components/common";
import { formatActivityDateTime } from "@/components/bookings/formatActivityDateTime";
import {
  bookingService,
  type AdminBookingDetailResponse,
} from "@/services/bookingService";
import { formatMoney } from "@/lib/money";
import { BookingAdminTimeline } from "./BookingAdminTimeline";
import { BookingAdminParties } from "./BookingAdminParties";
import { BookingAdminTechnicalRefs } from "./BookingAdminTechnicalRefs";
import { BookingAdminPaymentSection } from "./BookingAdminPaymentSection";
import { BookingCancellationSource } from "./BookingCancellationSource";
import { BookingDetailAmount } from "./BookingDetailAmount";
import { BookingHandshakePanel } from "./BookingHandshakePanel";
import {
  BookingPriceBreakdown,
  LineItemDiscountBadges,
} from "./BookingPriceBreakdown";
import { BookingStatusBadge } from "./BookingStatusBadge";
import {
  BookingDetailHeader,
  type BookingDetailHeaderMeta,
} from "./BookingDetailHeader";
import { ReceiptAdminAdjustModal } from "./ReceiptAdminAdjustModal";
import {
  ADMIN_DETAIL_TAB_PARAM,
  type AdminDetailTab,
} from "./bookingAdminDetailTabs";
import type { BookingListRow } from "./bookingViewModel";
import { bookingPartyCount } from "./bookingPartyCount";
import {
  lineItemStrikethroughTotalCents,
  sumBookingDiscountBreakdown,
} from "./sellerBookingRules";

interface BookingAdminDetailViewProps {
  row: BookingListRow;
  detail: AdminBookingDetailResponse;
  showHandshake: boolean;
  canAdjustReceiptTotal: boolean;
  isAdjustingReceipt: boolean;
  onAdjustReceipt: (
    newTotalCents: number,
    reason: string | null,
  ) => Promise<void>;
}

const PHONE_MASK = "••••••••";

function parseTab(value: string | null, hasChat: boolean): AdminDetailTab {
  if (value === "payment") return "payment";
  if (value === "chat" && hasChat) return "chat";
  return "overview";
}

export function BookingAdminDetailView({
  row,
  detail,
  showHandshake,
  canAdjustReceiptTotal,
  isAdjustingReceipt,
  onAdjustReceipt,
}: BookingAdminDetailViewProps) {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("bookings");
  const [searchParams, setSearchParams] = useSearchParams();

  const booking = detail.booking;

  const [revealedPhone, setRevealedPhone] = useState<string | null>(null);
  const [showPhoneConfirm, setShowPhoneConfirm] = useState(false);
  const [receiptAdjustOpen, setReceiptAdjustOpen] = useState(false);

  const localTimeSuffix = (time: string) =>
    t("adminDetail.localTime", { time });
  const visitLabels = row.visitAt
    ? formatActivityDateTime(
        row.visitAt,
        detail.activity.region_code,
        localTimeSuffix,
      )
    : null;
  const bookedAtLabel =
    formatActivityDateTime(
      row.createdAt,
      detail.activity.region_code,
      localTimeSuffix,
    )?.fullLabel ?? "—";
  const partyCount = bookingPartyCount(row);
  const partyLabel = t("guest", { count: partyCount });

  const lineItems = booking.items;
  const priceBreakdown = sumBookingDiscountBreakdown(
    lineItems.map((item) => ({
      listTotalPriceCents: item.list_total_price_cents,
      sellerDiscountCents: item.seller_discount_cents,
      originalTotalPriceCents: item.original_total_price_cents,
      flitripDiscountCents: item.discount_cents,
    })),
  );
  const pointsDiscountCents =
    booking.points_discount_cents ?? row.pointsDiscountCents ?? null;
  const customerPaidCents =
    booking.amount_paid_cents ?? row.amountPaidCents ?? null;
  const bookingLanguage = resolveLocale(booking.language);
  const receiptUploads = booking.partner_receipt_upload_history ?? [];
  const isReceiptBased = row.isReceiptBased;

  const reachedAccepted =
    row.status === "CANCELED" &&
    (isReceiptBased
      ? row.receiptTotalCents != null ||
        Boolean(booking.partner_receipt_url) ||
        receiptUploads.length > 0
      : booking.external_payment_id != null);

  const revealPhoneMutation = useMutation({
    mutationFn: () => bookingService.revealContactPhone(booking.id),
    onSuccess: (result) => {
      setRevealedPhone(result.contact_phone);
      setShowPhoneConfirm(false);
    },
    onError: (err) => {
      toastApiError(err, "generic", t("adminDetail.phone.revealError"));
    },
  });

  const activityName = row.activityName || detail.activity.name;

  const headerMeta: BookingDetailHeaderMeta[] = [
    ...(activityName ? [{ icon: Building2, label: activityName }] : []),
    ...(visitLabels
      ? [{ icon: Calendar, label: `${visitLabels.dateLabel} · ${visitLabels.timeLabel}` }]
      : []),
    ...(partyCount > 0 ? [{ icon: Users, label: partyLabel }] : []),
  ];

  const handshakeSessionId =
    row.activityRequiresHandshake && showHandshake
      ? row.handshakeSessionId
      : null;
  const hasChatTab = row.activityRequiresHandshake;
  const activeTab = parseTab(searchParams.get(ADMIN_DETAIL_TAB_PARAM), hasChatTab);
  // Mount the chat (and its queries) only once the tab is opened, then keep it.
  const [chatTabOpened, setChatTabOpened] = useState(activeTab === "chat");
  if (activeTab === "chat" && !chatTabOpened) {
    setChatTabOpened(true);
  }
  const setActiveTab = (tab: AdminDetailTab) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === "overview") next.delete(ADMIN_DETAIL_TAB_PARAM);
        else next.set(ADMIN_DETAIL_TAB_PARAM, tab);
        return next;
      },
      { replace: true },
    );
  };

  const headerAmount = isReceiptBased
    ? {
        label: t("detail.receipt"),
        value:
          row.receiptTotalCents != null
            ? formatMoney(row.receiptTotalCents)
            : null,
      }
    : customerPaidCents != null
      ? { label: t("detail.amountPaid"), value: formatMoney(customerPaidCents) }
      : {
          label: t("detail.total"),
          value:
            row.totalPriceCents != null ? formatMoney(row.totalPriceCents) : null,
        };

  const idPrefix = "booking-admin-detail";
  const tabs: { id: AdminDetailTab; label: string; icon: ReactNode }[] = [
    {
      id: "overview",
      label: t("adminDetail.tabs.overview"),
      icon: <LayoutList className="h-4 w-4" aria-hidden />,
    },
    {
      id: "payment",
      label: isReceiptBased
        ? t("adminDetail.sections.receipt")
        : t("adminDetail.sections.payment"),
      icon: isReceiptBased ? (
        <FileText className="h-4 w-4" aria-hidden />
      ) : (
        <CreditCard className="h-4 w-4" aria-hidden />
      ),
    },
    ...(hasChatTab
      ? [
          {
            id: "chat" as const,
            label: t("adminDetail.sections.chat"),
            icon: <MessageSquare className="h-4 w-4" aria-hidden />,
          },
        ]
      : []),
  ];
  const panelProps = (tab: AdminDetailTab) => ({
    id: `${idPrefix}-tabpanel-${tab}`,
    labelledBy: `${idPrefix}-tab-${tab}`,
    isActive: activeTab === tab,
  });

  const notes = [
    booking.customer_notes
      ? { label: t("detail.customerNotes"), value: booking.customer_notes }
      : null,
    booking.seller_notes
      ? { label: t("detail.sellerNotes"), value: booking.seller_notes }
      : null,
  ].filter((note): note is { label: string; value: string } => note != null);

  const overviewPanel = (
    <SectionCard title={t("adminDetail.sections.booking")} icon={ShoppingBag}>
      {row.status === "CANCELED" && row.cancellationSource ? (
        <BookingCancellationSource
          source={row.cancellationSource}
          variant="detail"
        />
      ) : null}

      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
        <div className="rounded-lg border border-primary-200/70 bg-primary-50/50 px-4 py-3 dark:bg-primary-50/30">
          <p className="text-xs font-medium text-primary-700">
            {t("detail.requestedVisit")}
          </p>
          <p className="mt-1 flex items-center gap-1.5 text-[15px] font-semibold capitalize tracking-tight text-surface-900">
            <Calendar className="h-4 w-4 shrink-0 text-primary-600" aria-hidden />
            {visitLabels?.dateLabel ?? "—"}
          </p>
          {visitLabels ? (
            <p className="mt-1 flex items-center gap-1.5 text-sm font-medium tabular-nums text-primary-800">
              <Clock className="h-4 w-4 shrink-0" aria-hidden />
              {visitLabels.timeLabel}
            </p>
          ) : null}
        </div>
        {partyCount > 0 ? (
          <div className="flex items-center gap-3 rounded-lg border border-surface-200 bg-surface-50 px-4 py-3 sm:flex-col sm:items-start sm:justify-center sm:gap-1 dark:bg-surface-100/40">
            <p className="text-xs font-medium text-surface-500">
              {t("detail.partySize")}
            </p>
            <p className="flex items-center gap-1.5 text-[15px] font-semibold tabular-nums text-surface-900">
              <Users className="h-4 w-4 text-surface-400" aria-hidden />
              {partyLabel}
            </p>
          </div>
        ) : null}
      </div>

      {lineItems.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs font-medium text-surface-500">
            {t("detail.services")}
          </p>
          <ul className="divide-y divide-surface-200/70 rounded-lg border border-surface-200">
            {lineItems.map((item) => {
              const strikeTotal = lineItemStrikethroughTotalCents({
                listTotalPriceCents: item.list_total_price_cents,
                sellerDiscountCents: item.seller_discount_cents,
                originalTotalPriceCents: item.original_total_price_cents,
                totalPriceCents: item.total_price_cents,
              });
              return (
                <li
                  key={item.id}
                  className="flex items-start justify-between gap-3 px-3.5 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-surface-900">
                      {item.activity_item_name?.trim() || t("unknownActivity")}
                    </p>
                    <p className="mt-0.5 text-xs tabular-nums text-surface-500">
                      {t("adminDetail.lineItems.qtyUnit", {
                        qty: item.quantity,
                        unit: formatMoney(item.unit_price_cents),
                      })}
                    </p>
                    <LineItemDiscountBadges
                      sellerDiscountCents={item.seller_discount_cents}
                      flitripDiscountCents={item.discount_cents}
                    />
                  </div>
                  <div className="shrink-0 text-right">
                    {strikeTotal != null ? (
                      <p className="text-xs tabular-nums text-surface-400 line-through">
                        {formatMoney(strikeTotal)}
                      </p>
                    ) : null}
                    <p className="text-sm font-semibold tabular-nums text-surface-900">
                      {formatMoney(item.total_price_cents)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
          {!isReceiptBased &&
          priceBreakdown != null &&
          (priceBreakdown.hasAnyDiscount || (pointsDiscountCents ?? 0) > 0) ? (
            <BookingPriceBreakdown
              breakdown={priceBreakdown}
              audience="admin"
              pointsDiscountCents={pointsDiscountCents}
              customerPaidCents={customerPaidCents}
              className="rounded-lg border border-surface-200 bg-surface-50 px-3.5 py-3 dark:bg-surface-100/40"
            />
          ) : !isReceiptBased && row.totalPriceCents != null ? (
            <div className="flex items-baseline justify-between gap-3 px-3.5">
              <span className="text-sm font-medium text-surface-700">
                {t("detail.total")}
              </span>
              <span className="text-base font-semibold tabular-nums text-surface-900">
                {formatMoney(row.totalPriceCents)}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-x-6 sm:grid-cols-2">
        <DetailRow icon={Globe} label={t("adminDetail.language")}>
          {supportedLanguageLabel(bookingLanguage)}
        </DetailRow>
        <DetailRow icon={Calendar} label={t("detail.createdAt")}>
          {bookedAtLabel}
        </DetailRow>
      </div>

      {notes.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {notes.map((note) => (
            <div
              key={note.label}
              className="rounded-lg border border-surface-200 bg-surface-50 px-3.5 py-3 dark:bg-surface-100/40"
            >
              <p className="flex items-center gap-1.5 text-xs font-medium text-surface-500">
                <NotebookPen className="h-3.5 w-3.5" aria-hidden />
                {note.label}
              </p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-surface-800">
                {note.value}
              </p>
            </div>
          ))}
        </div>
      ) : null}
    </SectionCard>
  );

  const chatPanel = handshakeSessionId ? (
    <SectionCard
      title={t("adminDetail.sections.chat")}
      icon={MessageSquare}
      description={t("adminDetail.chat.readOnlyHint")}
      contentClassName="space-y-0 p-0 sm:p-0"
    >
      <BookingHandshakePanel
        sessionId={handshakeSessionId}
        adminMode
        canInteract={false}
        canSendMessage={false}
        flush
      />
    </SectionCard>
  ) : (
    <SectionCard title={t("adminDetail.sections.chat")} icon={MessageSquare}>
      <EmptyState
        variant="dashed"
        size="sm"
        icon={MessageSquare}
        title={t("detail.noHandshake")}
      />
    </SectionCard>
  );

  const customerCard = (
    <SectionCard title={t("adminDetail.sections.customer")} icon={User}>
      {row.contactInfo.email ? (
        <DetailRow icon={Mail} label={t("detail.email")}>
          <a
            href={`mailto:${row.contactInfo.email}`}
            className="break-all text-primary-700 hover:text-primary-900"
          >
            {row.contactInfo.email}
          </a>
        </DetailRow>
      ) : null}
      {detail.has_contact_phone ? (
        <DetailRow icon={Phone} label={t("detail.phone")}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono">{revealedPhone ?? PHONE_MASK}</span>
            {!revealedPhone ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto px-2 py-1 text-primary-700"
                leftIcon={<Eye className="h-3.5 w-3.5" />}
                onClick={() => setShowPhoneConfirm(true)}
              >
                {t("adminDetail.phone.reveal")}
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto px-2 py-1 text-surface-600"
                leftIcon={<EyeOff className="h-3.5 w-3.5" />}
                onClick={() => setRevealedPhone(null)}
              >
                {t("adminDetail.phone.hide")}
              </Button>
            )}
          </div>
        </DetailRow>
      ) : null}
      {!row.contactInfo.email && !detail.has_contact_phone ? (
        <p className="text-sm text-surface-500">—</p>
      ) : null}
    </SectionCard>
  );

  return (
    <div className="space-y-4 sm:space-y-5">
      <BookingDetailHeader
        title={row.customerDisplayName}
        customerName={row.customerDisplayName}
        badge={
          <BookingStatusBadge
            status={row.status}
            handshakeStatus={row.handshakeStatus}
            size="lg"
          />
        }
        meta={headerMeta}
        actions={
          <BookingDetailAmount
            label={headerAmount.label}
            value={headerAmount.value}
            caption={t(`adminDetail.payment.models.${booking.activity_payment_model}`)}
          />
        }
      >
        <BookingAdminTimeline
          status={row.status}
          requiresHandshake={row.activityRequiresHandshake}
          reachedAccepted={reachedAccepted}
          createdAt={row.createdAt}
          updatedAt={booking.updated_at}
        />
      </BookingDetailHeader>

      <div className="grid items-start gap-4 sm:gap-5 xl:grid-cols-3">
        <div className="min-w-0 space-y-4 xl:col-span-2">
          <Tabs
            variant="underline"
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            ariaLabel={t("adminDetail.tabs.label")}
            idPrefix={idPrefix}
          />
          <DetailTabPanel {...panelProps("overview")} className="animate-slide-up">
            {overviewPanel}
          </DetailTabPanel>
          <DetailTabPanel {...panelProps("payment")} className="animate-slide-up">
            <BookingAdminPaymentSection
              row={row}
              detail={detail}
              canAdjustReceiptTotal={canAdjustReceiptTotal}
              isAdjustingReceipt={isAdjustingReceipt}
              onAdjustReceiptClick={() => setReceiptAdjustOpen(true)}
            />
          </DetailTabPanel>
          {hasChatTab ? (
            <DetailTabPanel {...panelProps("chat")} className="animate-slide-up">
              {chatTabOpened ? chatPanel : null}
            </DetailTabPanel>
          ) : null}
        </div>

        <aside className="min-w-0 space-y-4">
          {customerCard}
          <BookingAdminParties row={row} detail={detail} />
          <BookingAdminTechnicalRefs row={row} detail={detail} />
        </aside>
      </div>

      <ConfirmModal
        closeLabel={tCommon("close")}
        isOpen={showPhoneConfirm}
        onClose={() => {
          if (!revealPhoneMutation.isPending) {
            setShowPhoneConfirm(false);
          }
        }}
        onConfirm={() => revealPhoneMutation.mutate()}
        title={t("adminDetail.phone.confirmTitle")}
        message={t("adminDetail.phone.confirmMessage")}
        confirmText={t("adminDetail.phone.confirmAction")}
        cancelText={t("common:cancel")}
        isLoading={revealPhoneMutation.isPending}
      />

      {receiptAdjustOpen && row.receiptTotalCents != null ? (
        <ReceiptAdminAdjustModal
          currentTotalCents={row.receiptTotalCents}
          isSubmitting={isAdjustingReceipt}
          onSubmit={(totalCents, reason) => {
            void (async () => {
              try {
                await onAdjustReceipt(totalCents, reason);
                setReceiptAdjustOpen(false);
              } catch {
                // Toast already shown by the page handler; keep modal open.
              }
            })();
          }}
          onClose={() => {
            if (!isAdjustingReceipt) {
              setReceiptAdjustOpen(false);
            }
          }}
        />
      ) : null}
    </div>
  );
}
